<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { PhCheck, PhX } from '@phosphor-icons/vue'
import Modal from '@/components/ui/Modal.vue'
import { useWorkspaceStore } from '@/stores/workspace'
import type { FeatureFlag, PlanTier } from '@/types'

const props = defineProps<{
  open: boolean
  // When set, the modal pivots to that feature as the headline — both as
  // the "you tried to access X" hint and as the first row of the compare
  // table. When null, the modal renders the generic tier-compare with the
  // curated headline-feature list.
  feature?: FeatureFlag | null
}>()

const emit = defineEmits<{
  close: []
}>()

const route = useRoute()
const { t } = useI18n()
const workspaceStore = useWorkspaceStore()

const workspaceId = route.params.workspaceId as string

// Curated compare set. Order is display order. The user-pressed feature
// (props.feature) is hoisted to the top when present.
const COMPARE_FEATURES: FeatureFlag[] = ['catalog', 'collections', 'email', 'integrations', 'scheduler', 'customers']

const suggestedUpgrade = computed(() => {
  // If a feature was provided, target the cheapest tier that unlocks it.
  // Otherwise target the cheapest tier that unlocks any headline feature.
  if (props.feature) {
    return workspaceStore.getMinTierForFeature(props.feature)
  }
  for (const flag of COMPARE_FEATURES) {
    const candidate = workspaceStore.getMinTierForFeature(flag)
    if (candidate) return candidate
  }
  return null
})

const priceLabel = computed(() => {
  if (!suggestedUpgrade.value) return null
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(suggestedUpgrade.value.monthly)
})

const targetTierName = computed(() => {
  if (!suggestedUpgrade.value) return ''
  const tier = suggestedUpgrade.value.tier
  return t(`subscription.planName${tier.charAt(0).toUpperCase()}${tier.slice(1)}`)
})

const currentTierName = computed(() => {
  const tier = workspaceStore.currentWorkspace?.workspaceTier
  if (!tier || tier === 'free') return t('subscription.planNameFree')
  return t(`subscription.planName${tier.charAt(0).toUpperCase()}${tier.slice(1)}`)
})

// Walks the plans payload to read a feature's access for a given tier.
// Mirrors getMinTierForFeature's logic — keep them in sync if entitlement
// shape changes.
function readFeatureAccess(tierBlock: PlanTier | undefined, flag: FeatureFlag): 'full' | 'limited' | 'gated' | 'hidden' | null {
  if (!tierBlock) return null
  const parts = flag.split('.')
  let current: any = tierBlock.features
  for (const p of parts) {
    if (!current || typeof current !== 'object') return null
    current = current[p]
  }
  if (current && typeof current === 'object') return current.enabled ?? null
  if (typeof current === 'string') return current as 'full' | 'limited' | 'gated' | 'hidden'
  if (current === true) return 'full'
  if (current === false) return 'hidden'
  return null
}

interface CompareRow {
  flag: FeatureFlag
  label: string
  currentHas: boolean
  targetHas: boolean
}

const compareRows = computed<CompareRow[]>(() => {
  const tierMap = workspaceStore.availablePlans?.plans
  if (!tierMap || !suggestedUpgrade.value) return []

  const currentTierKey = workspaceStore.currentWorkspace?.workspaceTier || 'free'
  const currentBlock = tierMap[currentTierKey]
  const targetBlock = tierMap[suggestedUpgrade.value.tier]

  // Hoist the pressed feature to the top, then append the headline list,
  // dedupe, cap at 6 rows so the modal stays compact.
  const hoisted: FeatureFlag[] = []
  if (props.feature) hoisted.push(props.feature)
  for (const f of COMPARE_FEATURES) {
    if (!hoisted.includes(f)) hoisted.push(f)
  }

  const rows: CompareRow[] = []
  for (const flag of hoisted) {
    const cur = readFeatureAccess(currentBlock, flag)
    const tgt = readFeatureAccess(targetBlock, flag)
    const currentHas = cur === 'full' || cur === 'limited'
    const targetHas = tgt === 'full' || tgt === 'limited'
    // Only show rows where there's a meaningful delta. If both tiers have
    // the feature we hide it (no reason to upsell on something already
    // unlocked). If neither has it, also hide (not a selling point).
    if (currentHas && targetHas) continue
    if (!currentHas && !targetHas) continue
    rows.push({
      flag,
      label: t(`upgradeCard.feature.${flag}`),
      currentHas,
      targetHas,
    })
    if (rows.length >= 6) break
  }
  return rows
})

function handleViewPlans() {
  emit('close')
}
</script>

<template>
  <Modal :open="open" size="md" @close="emit('close')">
    <div class="upgrade-modal">
      <header class="upgrade-modal-header">
        <h3 class="upgrade-modal-title">
          {{
            suggestedUpgrade
              ? t('upgrade.unlockWith', { plan: targetTierName })
              : t('upgrade.featureLocked')
          }}
        </h3>
        <p v-if="suggestedUpgrade" class="upgrade-modal-price">
          {{ t('upgrade.fromPerMonth', { price: priceLabel }) }}
        </p>
        <p v-else class="upgrade-modal-desc">{{ t('upgrade.featureLockedDesc') }}</p>
      </header>

      <div v-if="compareRows.length > 0" class="upgrade-compare">
        <div class="upgrade-compare-headers">
          <div class="upgrade-compare-col-label upgrade-compare-col-current">
            {{ currentTierName }}
          </div>
          <div class="upgrade-compare-col-label upgrade-compare-col-target">
            {{ targetTierName }}
          </div>
        </div>

        <ul class="upgrade-compare-rows">
          <li
            v-for="row in compareRows"
            :key="row.flag"
            class="upgrade-compare-row"
          >
            <span class="upgrade-compare-feature">{{ row.label }}</span>
            <span class="upgrade-compare-cell upgrade-compare-cell-current">
              <PhX v-if="!row.currentHas" :size="14" weight="bold" class="cell-x" />
              <PhCheck v-else :size="14" weight="bold" class="cell-check" />
            </span>
            <span class="upgrade-compare-cell upgrade-compare-cell-target">
              <PhCheck v-if="row.targetHas" :size="14" weight="bold" class="cell-check" />
              <PhX v-else :size="14" weight="bold" class="cell-x" />
            </span>
          </li>
        </ul>
      </div>

      <div v-else class="upgrade-compare-empty">
        <p>{{ t('upgrade.featureLockedDesc') }}</p>
      </div>

      <div class="upgrade-modal-actions">
        <RouterLink
          class="btn-view-plans"
          :to="{ path: `/${workspaceId}/subscription` }"
          @click="handleViewPlans"
        >
          {{ t('upgrade.viewPlans') }}
        </RouterLink>
        <button class="btn-close" @click="emit('close')">
          {{ t('upgrade.maybeLater') }}
        </button>
      </div>
    </div>
  </Modal>
</template>

<style scoped>
.upgrade-modal {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 4px 0;
}

.upgrade-modal-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: center;
}

.upgrade-modal-title {
  font-size: 18px;
  font-weight: 700;
  color: #021E30;
  margin: 0;
  line-height: 1.25;
}

.upgrade-modal-price {
  font-size: 14px;
  font-weight: 600;
  color: #B45309;
  margin: 0;
}

.upgrade-modal-desc {
  font-size: 14px;
  color: #71717A;
  margin: 0;
}

.upgrade-compare {
  border: 1px solid #E4E4E7;
  border-radius: 12px;
  overflow: hidden;
}

.upgrade-compare-headers {
  display: grid;
  grid-template-columns: 1fr 80px 80px;
  background-color: #FAFAFA;
  border-bottom: 1px solid #E4E4E7;
}

.upgrade-compare-col-label {
  padding: 10px 12px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: center;
  color: #71717A;
}

.upgrade-compare-col-label:first-child {
  text-align: left;
  visibility: hidden;
}

.upgrade-compare-col-target {
  color: #B45309;
  background-color: #FEF3C7;
}

.upgrade-compare-rows {
  list-style: none;
  margin: 0;
  padding: 0;
}

.upgrade-compare-row {
  display: grid;
  grid-template-columns: 1fr 80px 80px;
  align-items: center;
  border-bottom: 1px solid #F4F4F5;
}

.upgrade-compare-row:last-child {
  border-bottom: none;
}

.upgrade-compare-feature {
  padding: 12px 14px;
  font-size: 14px;
  font-weight: 500;
  color: #18181B;
}

.upgrade-compare-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 0;
}

.upgrade-compare-cell-target {
  background-color: #FEFBEB;
}

.cell-check {
  color: #16A34A;
}

.cell-x {
  color: #D4D4D8;
}

.upgrade-compare-empty {
  padding: 20px;
  text-align: center;
  font-size: 14px;
  color: #71717A;
}

.upgrade-modal-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.btn-view-plans {
  width: 100%;
  padding: 12px 16px;
  background-color: #021E30;
  color: #FFFFFF;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease;
  font-family: inherit;
}

.btn-view-plans:hover {
  background-color: #0a2e44;
}

.btn-close {
  width: 100%;
  padding: 10px 16px;
  background: transparent;
  color: #71717A;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  transition: background-color 0.15s ease;
}

.btn-close:hover {
  background-color: #F9F9F9;
}
</style>
